<template>
  <div class="football-score-skeleton">
    <!-- 顶部控制栏骨架 -->
    <div class="top-bar">
      <div class="left-actions">
        <div class="btn" v-for="i in 4" :key="'btn-'+i"></div>
      </div>
      <div class="right-actions">
        <div class="pill" v-for="i in 4" :key="'pill-'+i"></div>
      </div>
    </div>

    <!-- 联赛标签骨架 -->
    <div class="tabs">
      <div class="tab" v-for="i in 8" :key="'tab-'+i"></div>
    </div>

    <div class="content">
      <!-- 左侧比赛列表骨架 -->
      <div class="list">
        <!-- 表头骨架 -->
        <div class="header">
          <div class="col time"></div>
          <div class="col status"></div>
          <div class="col teams"></div>
          <div class="col small" v-for="i in 5" :key="'small-'+i"></div>
        </div>
        <!-- 行骨架 -->
        <div class="row" v-for="i in rows" :key="'row-'+i">
          <div class="cell time">
            <div class="bar sm w50"></div>
            <div class="bar sm w60"></div>
          </div>
          <div class="cell status"><div class="dot"></div></div>
          <div class="cell teams">
            <div class="bar md w80"></div>
            <div class="bar md w70"></div>
          </div>
          <div class="cell small" v-for="j in 5" :key="'s-'+i+'-'+j">
            <div class="bar sm w40"></div>
            <div class="bar sm w40"></div>
          </div>
        </div>
      </div>

      <!-- 右侧详情骨架 -->
      <div class="detail">
        <div class="video"></div>
        <div class="tabs">
          <div class="tab w60"></div>
          <div class="tab w80"></div>
          <div class="tab w60"></div>
        </div>
        <div class="panel">
          <div class="line" v-for="i in 6" :key="'line-'+i"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * 足球比分页面骨架屏
 * - 匹配 ScoreFootball 布局（顶部按钮 + 联赛标签 + 左侧表格 + 右侧详情）
 */
const props = defineProps<{ rows?: number }>()
const rows = props.rows ?? 12
</script>

<style scoped lang="scss">
.football-score-skeleton {
  // 使用与实际页面一致的颜色变量（SCSS 常量）
  $sk-bg-primary: #f5f6fa;
  $sk-bg-secondary: #e8eaed;
  $sk-bg-white: #ffffff;
  $sk-border-color: #edf0f5;
  $sk-shadow-light: rgba(0, 0, 0, 0.02);
  $sk-radius: 10px;
  
  // 适配主题颜色
  background: $scoreFootball-page-bg;
  
  .shimmer { animation: shimmer 1.4s ease-in-out infinite; }

  .top-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;

    .left-actions { display: flex; gap: 12px; }
    .right-actions { display: flex; gap: 10px; }

    .btn {
      width: 64px; height: 36px; border-radius: 18px; background: $sk-bg-primary;
      box-shadow: 0 0 10px $sk-shadow-light;
      animation: shimmer 1.4s ease-in-out infinite;
    }
    .pill {
      width: 72px; height: 30px; border-radius: 16px; background: $sk-bg-white;
      border: 1px solid $sk-border-color; animation: shimmer 1.4s ease-in-out infinite;
    }
  }

  .tabs {
    display: flex; gap: 12px; padding: 10px; background: $sk-bg-white;
    border-radius: 12px; margin-bottom: 10px; overflow-x: auto;
    .tab { width: 96px; height: 28px; border-radius: 14px; background: $sk-bg-primary; animation: shimmer 1.4s ease-in-out infinite; }
    .tab.w60 { width: 60px; }
    .tab.w80 { width: 80px; }
  }

  .content { display: flex; gap: 10px; }

  .list { width: 815px; background: $sk-bg-white; border-radius: $sk-radius; overflow: hidden; }

  .header { display: flex; align-items: center; height: 53px; border-bottom: 1px solid $sk-border-color; background: $sk-bg-white; }
  .header .col { animation: shimmer 1.4s ease-in-out infinite; height: 18px; border-radius: 4px; background: $sk-bg-primary; }
  .header .time { flex: 1.5; margin-left: 20px; width: 120px; }
  .header .status { flex: .5; width: 60px; }
  .header .teams { flex: 2; width: 200px; }
  .header .small { flex: .4; width: 60px; margin: 0 6px; }
  .header .betting { flex: 2; }

  .row { display: flex; min-height: 46px; border-bottom: 1px solid $sk-border-color; background: $sk-bg-white; }
  .row:nth-child(2n) { background: #fafbff; }

  .cell { display: flex; align-items: center; justify-content: center; }
  .cell.time { flex: 1.5; flex-direction: column; align-items: flex-start; padding-left: 70px; position: relative; }
  .cell.time::after { content: ''; position: absolute; left: 50px; width: 4px; height: 32px; top: 50%; margin-top: -16px; background: #dfe7ff; border-radius: 2px; }
  .cell.status { flex: .5; }
  .cell.teams { flex: 2; flex-direction: column; align-items: flex-start; padding-left: 25px; gap: 8px; }
  .cell.small { flex: .4; flex-direction: column; gap: 6px; }

  .bar { height: 12px; background: $sk-bg-primary; border-radius: 6px; animation: shimmer 1.4s ease-in-out infinite; }
  .bar.sm { height: 10px; }
  .bar.md { height: 12px; }
  .bar.w40 { width: 40px; }
  .bar.w50 { width: 50px; }
  .bar.w60 { width: 60px; }
  .bar.w70 { width: 70px; }
  .bar.w80 { width: 80px; }

  .dot { width: 18px; height: 18px; border-radius: 50%; background: $sk-bg-primary; animation: shimmer 1.4s ease-in-out infinite; }

  .detail { width: 375px; border-radius: $sk-radius; }
  .detail .video { height: 210px; background: $sk-bg-primary; border-radius: 8px; animation: shimmer 1.4s ease-in-out infinite; }
  .detail .tabs { display: flex; gap: 8px; margin: 12px 0; }
  .detail .panel { background: $sk-bg-white; border-radius: 8px; padding: 12px; }
  .detail .panel .line { height: 12px; border-radius: 6px; background: $sk-bg-primary; margin-bottom: 10px; animation: shimmer 1.4s ease-in-out infinite; }

  @keyframes shimmer {
    0% { opacity: 1; }
    50% { opacity: .6; }
    100% { opacity: 1; }
  }
}
</style>
